import React, { useEffect, useRef } from 'react';
import { EditorContent, Editor } from '@tiptap/react';
import { Box, BoxProps, useStyleConfig } from '@chakra-ui/react';
import { Prose } from '@nikolovlazar/chakra-ui-prose';

/**
 * This is just Tiptap's EditorContent but with className set on the inner
 * (contentEditable) div and not the wrapping div. Ideally we'd not wrap the
 * contentEditable at all, but it seems Tiptap provides no way to disable the
 * wrapping div.
 */
function BaseEditorPane(props: { editor: Editor; className?: string }) {
  const { editor, className } = props;

  const editorRef = useRef(editor);
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    editorRef.current.setOptions({
      editorProps: { attributes: { class: className ?? '' } },
    });
  }, [className]);

  return <EditorContent editor={editor} />;
}

type Props = Expand<BoxProps & { editor: Editor }>;

/**
 * Wraps the component above and uses Chakra's theme system to ensure the
 * contentEditable div gets styled to look like a Textarea.
 */
export function EditorPane(props: Props) {
  const { __css, height, ...otherProps } = props;
  const styles = useStyleConfig('Textarea');
  const resolvedHeight = height ?? 'auto';
  const shouldScroll = resolvedHeight !== 'auto';
  return (
    <Prose as="div">
      <Box
        as={BaseEditorPane}
        __css={styles}
        height={resolvedHeight}
        overflow={shouldScroll ? 'auto' : undefined}
        sx={{ a: { cursor: 'inherit' } }}
        {...otherProps}
      />
    </Prose>
  );
}
